<template>
  <div class="assignments-container">
    <eg-b-container>
      <section class="p-0">
        <div>
          <eg-b-row>
            <eg-b-col>
              <page-form-wrapper
                v-if="currentStep === 1"
                :title="`Import from ${LMS_PROVIDERS[platformKey].label} ${stepLabel}`"
              >
                <submission-select-form
                  :workspace-id="workspaceId"
                  :platform-key="platformKey"
                  :selected-class-ids.sync="selectedClassIds"
                  :selected-assignments.sync="selectedAssignments"
                  :selected-submissions-ids.sync="selectedSubmissionsIds"
                  @loaded="loading = false"
                />

                <template #footer>
                  <eg-button
                    class="flex-shrink-0"
                    type="primary"
                    right-icon="arrow-right"
                    :is-loading="isSubmitting"
                    :disabled="
                      !selectedClassIds.length ||
                      !selectedAssignments.length ||
                      !selectedSubmissionsIds.length
                    "
                    @click="nextStep"
                  >
                    <span>{{ continueButtonLabel }}</span>
                  </eg-button>
                </template>
              </page-form-wrapper>
              <page-form-wrapper
                v-if="currentStep === 2"
                :title="`Import from ${LMS_PROVIDERS[platformKey].label} 2/2`"
              >
                <rubric-with-grading-details-form
                  v-model="gradingSettings"
                  :additional-source-materials="
                    draftSourceMaterial ? [draftSourceMaterial] : []
                  "
                  :is-loading-source-material="isLoadingDraftSourceMaterial"
                />
                <template #footer>
                  <eg-button
                    class="flex-shrink-0"
                    type="secondary"
                    @click="prevStep"
                  >
                    <span>Back</span>
                  </eg-button>
                  <eg-button
                    class="flex-shrink-0 ml-2"
                    type="primary"
                    right-icon="arrow-right"
                    :is-loading="isSubmitting"
                    loading-text="Start Grading"
                    :disabled="
                      !selectedClassIds.length ||
                      !selectedAssignments.length ||
                      !selectedSubmissionsIds.length ||
                      !gradingSettings.selectedRubric
                    "
                    @click="onSubmit"
                  >
                    <span>Start Grading</span>
                  </eg-button>
                </template>
              </page-form-wrapper>
            </eg-b-col>
          </eg-b-row>
        </div>
      </section>
    </eg-b-container>
    <lms-import-errors-modal
      v-if="showErrorsModal"
      :submissions="failedSubmissions"
      :loading="false"
      @close="handleModalClose"
    />
  </div>
</template>

<script>
import { formatAuthorValue } from "@/utils/format";
import {
  LMS_PROVIDERS,
  WORKSPACE_PLATFORM_LOCATION_URL,
  LSK_SUGGESTION_FORM_SELECTED_RUBRIC,
} from "@/constants";
import importApi from "@/api/requests/import";
import errorHandler from "@/components/imports/errorHandler";
import PageFormWrapper from "@/views/dashboard/workspace-integration/assignments/page-form-wrapper.vue";
import SubmissionSelectForm from "@/components/imports/submissions-select-form/submissions-select-form.vue";
import RubricWithGradingDetailsForm from "@/components/rubrics/rubric_with_grading_details_form/rubric_with_grading_details_form.vue";
import LmsImportErrorsModal from "@/components/essay/modals/lms_import_errors_modal.vue";

export default {
  name: "AssignmentsForm",
  components: {
    SubmissionSelectForm,
    PageFormWrapper,
    RubricWithGradingDetailsForm,
    LmsImportErrorsModal,
  },
  inject: ["workspacesProvider"],

  data() {
    return {
      isLoadingDraftSourceMaterial: false,
      currentStep: 1,
      LMS_PROVIDERS,
      loading: false,
      selectedClassIds: [],
      selectedAssignments: [],
      selectedSubmissionsIds: [],
      draftSourceMaterial: null,
      gradingSettings: {
        selectedRubric: null,
        selectedSourceMaterial: null,
        gradingDetails: {},
      },
      isSubmitting: false,
      showErrorsModal: false,
      failedSubmissions: [],
      importedFolderId: null,
    };
  },
  computed: {
    platformKey() {
      return this.$route.params.platformKey;
    },
    workspaceId() {
      return this.$route.params.id;
    },
    continueButtonLabel() {
      return this.isSelectedAssignmentImported
        ? "Import"
        : "Continue Grading Setup";
    },
    stepLabel() {
      return this.isSelectedAssignmentImported ? "" : "1/2";
    },
    isSelectedAssignmentImported() {
      return this.selectedAssignments[0]?.imported;
    },
  },

  watch: {
    "$route.params.id": {
      handler() {
        this.selectedAssignments = [];
        this.selectedClassIds = [];
        this.selectedSubmissionsIds = [];
        this.currentStep = 1;
      },
    },
    selectedAssignments() {
      this.selectedSubmissionsIds = [];
    },
  },

  methods: {
    formatAuthorValue,
    nextStep() {
      if (this.isSelectedAssignmentImported) {
        return this.onSubmit();
      }

      this.currentStep = this.currentStep + 1;

      if (this.currentStep === 2) {
        this.getDraftSourceMaterial();
        this.$eventBus.$emit("onboarding:clicked:lmsImportStep2");
      }
    },
    prevStep() {
      this.currentStep = this.currentStep - 1;
    },
    async handleModalClose() {
      this.showErrorsModal = false;
      this.failedSubmissions = [];

      await this.redirectToFolder();
    },
    async redirectToFolder() {
      if (this.importedFolderId) {
        await this.workspacesProvider.init();
        const workspaceUrl = WORKSPACE_PLATFORM_LOCATION_URL[this.platformKey];
        await this.$router.push({
          name: "workspace",
          params: {
            workspaceKey: workspaceUrl,
            id: this.importedFolderId,
          },
        });
      }
    },
    async onSubmit() {
      this.isSubmitting = true;
      const payload = {
        submissionsIds: this.selectedSubmissionsIds.map((id) => `${id}`),
      };

      if (!this.isSelectedAssignmentImported) {
        payload.gradingDetails = this.gradingSettings.gradingDetails;
      }

      try {
        const { data } = await importApi[this.platformKey].importSubmissions(
          this.workspaceId,
          this.selectedClassIds[0],
          this.selectedAssignments[0].id,
          payload
        );

        const failedSubmissions = data.filter((item) => !item.imported);
        const importedItem = data.find((item) => !!item.folderId);
        this.importedFolderId = importedItem ? importedItem.folderId : null;

        if (failedSubmissions.length > 0) {
          // Show modal with errors table
          this.failedSubmissions = failedSubmissions;
          this.showErrorsModal = true;
        } else {
          this.$showToastSuccess(
            `Student assignments were imported successfully and are queued for grading.`,
            { title: "Import successful" }
          );
          await this.redirectToFolder();
        }

        localStorage.removeItem(LSK_SUGGESTION_FORM_SELECTED_RUBRIC);
      } catch (error) {
        errorHandler({
          vm: this,
          error,
          title: "Import Error",
          message:
            "There was a problem import submissions from the classroom. Please try again",
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async getDraftSourceMaterial() {
      try {
        this.isLoadingDraftSourceMaterial = true;
        const { data } = await importApi[this.platformKey].getDraftAssignment(
          this.workspaceId,
          this.selectedClassIds[0],
          this.selectedAssignments[0].id
        );
        this.draftSourceMaterial = data;
        this.gradingSettings.selectedSourceMaterial = data;
        this.gradingSettings.gradingDetails.sourceMaterialId = data.id;
      } catch (error) {
        this.$showToastError(error.response?.data?.error);
      } finally {
        this.isLoadingDraftSourceMaterial = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.assignments-container {
  position: relative;
  height: 100%;
  margin-bottom: 100px;
  min-height: 100vh;
}
</style>
